import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password) => 
    api.post('/accounts/users/login/', { username, password }),
  register: (userData) => 
    api.post('/accounts/users/', userData),
  logout: () => 
    api.post('/accounts/users/logout/'),
  getCurrentUser: () => 
    api.get('/accounts/users/me/'),
  passwordReset: (email) =>
    api.post('/accounts/users/password_reset/', { email }),
  passwordResetConfirm: (data) =>
    api.post('/accounts/users/password_reset_confirm/', data),
  sendVerification: (email) =>
    api.post('/accounts/users/send_verification/', email ? { email } : {}),
  verifyEmail: (uid, token) =>
    api.post('/accounts/users/verify_email/', { uid, token }),
};

// Event APIs
export const eventAPI = {
  getAll: () => 
    api.get('/events/'),
  getUpcoming: (days = 7) => 
    api.get(`/events/upcoming/?days=${days}`),
  getMyEvents: () => 
    api.get('/events/my_events/'),
  getById: (id) => 
    api.get(`/events/${id}/`),
  create: (eventData) => 
    api.post('/events/', eventData),
  getChanges: (sinceIso) =>
    api.get(`/events/changes/?since=${encodeURIComponent(sinceIso)}`),
  update: (id, eventData) => 
    api.put(`/events/${id}/`, eventData),
  delete: (id) => 
    api.delete(`/events/${id}/`),
  respond: (id, response) => 
    api.post(`/events/${id}/respond/`, { response }),
  removeResponse: (id) =>
    api.post(`/events/${id}/remove-response/`),
  getResponses: (id) => 
    api.get(`/events/${id}/responses/`),
  retweet: (id) => 
    api.post(`/events/${id}/retweet/`),
  repost: (id) =>
    api.post(`/events/${id}/repost/`),
  cancel: (id, data) =>
    api.post(`/events/${id}/cancel/`, data),
};

// Friendship APIs
export const friendshipAPI = {
  getMyFriends: () => 
    api.get('/friendships/friendships/my_friends/'),
  removeFriend: (id) => 
    api.delete(`/friendships/friendships/${id}/remove_friend/`),
  getIncomingRequests: () => 
    api.get('/friendships/requests/incoming/'),
  getOutgoingRequests: () => 
    api.get('/friendships/requests/outgoing/'),
  sendRequest: (toUser) => {
    // Accept either a numeric user id or a username string
    if (typeof toUser === 'number') {
      return api.post('/friendships/requests/send/', { to_user_id: toUser });
    }
    return api.post('/friendships/requests/send/', { to_username: toUser });
  },
  acceptRequest: (id) => 
    api.post(`/friendships/requests/${id}/accept/`),
  rejectRequest: (id) => 
    api.post(`/friendships/requests/${id}/reject/`),
};

// User APIs
export const userAPI = {
  getById: (id) => 
    api.get(`/accounts/users/${id}/`),
  getByUsername: (username) =>
    api.get(`/accounts/users/by-username/?username=${encodeURIComponent(username)}`),
  updateProfile: (id, profileData) => {
    // If profileData contains a File (profile_photo), send as multipart/form-data
    if (profileData && profileData.profile_photo instanceof File) {
      const fd = new FormData();
      Object.keys(profileData).forEach((k) => {
        if (profileData[k] !== undefined && profileData[k] !== null) {
          fd.append(k, profileData[k]);
        }
      });
      return api.patch(`/accounts/users/${id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.patch(`/accounts/users/${id}/`, profileData);
  },
};

export default api;
